import type { MatchState } from '../sim/state';
import type { SimEvent } from '../sim/events';
import type { SimConfig } from '../config/simConfig';
import type { RestartKind, TeamIdx, Vec2 } from '../types';
import { PITCH } from '../config/pitchConfig';
import { startRestart } from './phase';

// Out-of-play → restarts (§6.2). Pure. Runs after detectGoal (so a ball over the goal line here is a
// no-goal): touchline ⇒ THROW_IN (11v11) / KICK_IN (futsal); goal line ⇒ CORNER (defender out) /
// GOAL_KICK (attacker out), by lastTouch. A pending shot that goes out over the goal line emits MISS.

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Detect + apply an out-of-play restart. Returns true if the ball was out this tick. */
export function detectOutOfPlay(state: MatchState, events: SimEvent[], cfg: SimConfig): boolean {
  if (state.mode.teamSize <= cfg.rules.WALLED_MAX_SIZE) return false; // D40: cage football — no out, the boards play
  const pitch = PITCH[state.mode.teamSize];
  const r = cfg.ball.RADIUS;
  const b = state.ball;
  const halfLen = pitch.length / 2;
  const halfWid = pitch.width / 2;
  const lt = state.ball.lastTouch;

  // whole ball over a goal line (not a goal — detectGoal already ran)
  if (Math.abs(b.pos.x) - r > halfLen) {
    if (state.ball.shotPending && lt) events.push({ t: 'MISS', entity: lt.entity }); // a shot that ended out (§6.2)
    const side = (b.pos.x > 0 ? 1 : -1) as 1 | -1;
    const attackingTeam: TeamIdx = state.attackDir[0] === side ? 0 : 1; // the team attacking that goal
    const defendingTeam: TeamIdx = attackingTeam === 0 ? 1 : 0;
    if (lt && lt.team === attackingTeam) {
      startRestart(state, events, cfg, 'GOAL_KICK', defendingTeam, { x: side * (halfLen - pitch.goalAreaDepth), y: 0 });
    } else {
      startRestart(state, events, cfg, 'CORNER', attackingTeam, { x: side * halfLen, y: (b.pos.y >= 0 ? 1 : -1) * halfWid });
    }
    return true;
  }

  // whole ball over a touchline
  if (Math.abs(b.pos.y) - r > halfWid) {
    const kind: RestartKind = state.mode.teamSize >= 11 ? 'THROW_IN' : 'KICK_IN';
    const team: TeamIdx = lt && lt.team === 0 ? 1 : 0; // opponent of the last toucher
    const spot: Vec2 = { x: clamp(b.pos.x, -halfLen, halfLen), y: (b.pos.y >= 0 ? 1 : -1) * halfWid };
    startRestart(state, events, cfg, kind, team, spot);
    return true;
  }

  return false;
}
